import type { TranslationKey } from "@/localization/resources/keys.ts";

export interface PasswordRule {
	key: string; // maps to i18n step3.passwordRules.<key>
	labelKey: TranslationKey;
	test: (pw: string) => boolean;
}

const MIN_LENGTH = 12;

// Keycloak and the backend validator classify UTF-16 code units via java.lang.Character, not code
// points: an umlaut is a letter, while a supplementary character such as U+1D400 is two surrogate
// halves that both count as special characters. Testing each code unit separately reaches the same
// verdict. \p{Uppercase} / \p{Lowercase} match Character.isUpperCase / isLowerCase exactly.
const UPPERCASE = /\p{Uppercase}/u;
const LOWERCASE = /\p{Lowercase}/u;
const DIGIT = /\p{Nd}/u;
const SPECIAL_CHAR = /[^\p{L}\p{Nd}]/u;

const someCodeUnit = (pw: string, matches: RegExp): boolean => pw.split("").some((c) => matches.test(c));

export const PASSWORD_RULES: PasswordRule[] = [
	{ key: "minLength", labelKey: "step3.passwordRules.minLength", test: (pw) => pw.length >= MIN_LENGTH },
	{ key: "uppercase", labelKey: "step3.passwordRules.uppercase", test: (pw) => someCodeUnit(pw, UPPERCASE) },
	{ key: "lowercase", labelKey: "step3.passwordRules.lowercase", test: (pw) => someCodeUnit(pw, LOWERCASE) },
	{ key: "digit", labelKey: "step3.passwordRules.digit", test: (pw) => someCodeUnit(pw, DIGIT) },
	{ key: "specialChar", labelKey: "step3.passwordRules.specialChar", test: (pw) => someCodeUnit(pw, SPECIAL_CHAR) }
];

export const countMetRules = (pw: string): number => PASSWORD_RULES.filter((r) => r.test(pw)).length;

/** Every rule of the checklist is satisfied. */
export const rulesMet = (pw: string): boolean => countMetRules(pw) === PASSWORD_RULES.length;

/**
 * The password must not contain the e-mail address of the user (which is also the Keycloak username).
 * Mirrors the `notEmail` / `notUsername` / `notContainsUsername` Keycloak policies.
 */
export const containsEmail = (pw: string, email: string): boolean =>
	email.length > 0 && pw.toLowerCase().includes(email.toLowerCase());

/** The password can be submitted: checklist complete and no e-mail address inside. */
export const allRulesMet = (pw: string, email: string): boolean => rulesMet(pw) && !containsEmail(pw, email);
